import React, { useState } from "react";
import {
  X,
  User,
  Briefcase,
  Calendar,
  CheckCircle,
  MapPin,
  Clock,
  Send,
} from "lucide-react";
import { format } from "date-fns";
import {
  confirmEmployee,
  extendProbation,
} from "../../store/slices/hrDashboardSlice";
import { RootDispatch } from "../../store";
import { useDispatch } from "react-redux";

// --- TYPES ---
interface ConfirmationItem {
  _id: string;
  name: string;
  department?: string;
  location?: string;
  designation: string;
  employmentStatus: string;
  dateOfJoining: string;
  dateOfConfirmation: string;
  probationDays?: number;
  mobile?: string;
  email?: string;
}

interface ConfirmationDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  confirmations: ConfirmationItem[];
}

// Helper component for clean detail display
const DetailItem = ({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string;
  value: string;
  icon: React.ElementType;
  color: string;
}) => (
  <div className="flex flex-col p-2 bg-gray-50 rounded-lg border border-gray-100">
    <span className="text-xs font-medium text-slate-500 flex items-center mb-1">
      <Icon className={`h-3 w-3 mr-1 ${color}`} />
      {label}
    </span>
    <span className="font-bold text-gray-800 text-base leading-tight">
      {value}
    </span>
  </div>
);

// 1. Confirmation Section (Confirm)
const ConfirmationSection = ({
  employee,
  onCancel,
  onConfirm,
}: {
  employee: ConfirmationItem;
  onCancel: () => void;
  onConfirm: (payload: any) => void;
}) => {
  const [managerRemark, setManagerRemark] = useState("");

  const handleSubmit = () => {
    const payload = {
      employeeId: employee._id,
      action: "CONFIRM",
      confirmationDate: employee.dateOfConfirmation,
      managerRemark: managerRemark,
      timestamp: new Date().toISOString(),
    };
    onConfirm(payload);
    onCancel();
  };

  return (
    <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg w-full">
      <h4 className="text-lg font-bold text-green-700 mb-3 flex items-center">
        <CheckCircle className="h-5 w-5 mr-2" /> Confirm{" "}
        <span className="font-extrabold">{employee.name}</span>
      </h4>

      {/* Manager Remark Input */}
      <label className="block mb-4">
        <span className="text-sm font-medium text-green-800">
          Manager Confirmation Remark*
        </span>
        <textarea
          value={managerRemark}
          onChange={(e) => setManagerRemark(e.target.value)}
          placeholder="Enter a positive remark confirming the employee's performance and status."
          rows={3}
          className="mt-1 block w-full rounded-md border-green-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-50 p-2 text-sm"
        />
      </label>

      <div className="flex justify-end space-x-3 border-t border-green-200 pt-3">
        <button
          onClick={onCancel}
          className="text-sm font-medium text-gray-700 hover:text-gray-900 px-3 py-2 rounded-lg transition"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={!managerRemark.trim()}
          className="bg-green-600 hover:bg-green-700 text-white font-semibold text-sm py-2 px-4 rounded-lg shadow-md transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Send className="h-4 w-4 mr-1 inline-block" /> Confirm Employee
        </button>
      </div>
    </div>
  );
};

// 2. Extension Section (Extend) - Corrected Spacing, Labels, and Dimmer Colors
const ExtensionSection = ({
  employee,
  onCancel,
  onExtend,
}: {
  employee: ConfirmationItem;
  onCancel: () => void;
  onExtend: (payload: any) => void;
}) => {
  const [extendTill, setExtendTill] = useState("");
  const [employeeRemark, setEmployeeRemark] = useState("");
  const [managerRemark, setManagerRemark] = useState("");

  const handleSubmit = () => {
    const payload = {
      employeeId: employee._id,
      action: "EXTEND",
      newConfirmationDate: extendTill,
      managerRemark: managerRemark,
      employeeReferenceRemark: employeeRemark,
      timestamp: new Date().toISOString(),
    };
    onExtend(payload);
    onCancel();
  };

  return (
    <div className="mt-6 p-4 bg-yellow-50 border border-yellow-300 rounded-lg w-full">
      <h4 className="text-lg font-bold text-yellow-800 mb-4 flex items-center">
        <Clock className="h-5 w-5 mr-2 text-yellow-600" /> Extend Probation for{" "}
        <span className="font-extrabold">{employee.name}</span>
      </h4>

      <div className="space-y-3">
        {/* Extend Till Date */}
        <label className="block">
          <span className="text-sm font-medium text-yellow-800">
            Extend Till Date*
          </span>
          <input
            type="date"
            value={extendTill}
            min={format(new Date(), "yyyy-MM-dd")}
            onChange={(e) => setExtendTill(e.target.value)}
            // Neutral focus/border colors for inputs
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-600 focus:ring focus:ring-yellow-600 focus:ring-opacity-50 p-2"
          />
        </label>

        {/* Employee Remark - Simplified Label */}
        <label className="block">
          <span className="text-sm font-medium text-yellow-800">
            Remark for Employee
          </span>
          <textarea
            value={employeeRemark}
            onChange={(e) => setEmployeeRemark(e.target.value)}
            placeholder="e.g., Summary of employee's self-assessment or performance notes."
            rows={2}
            className="mt-1 block w-full rounded-md border-red-300 shadow-sm focus:border-red-500 focus:ring focus:ring-red-500 focus:ring-opacity-50 p-2"
          />
        </label>

        {/* Manager Remark - Simplified Label and Kept required status */}
        <label className="block">
          <span className="text-sm font-medium text-yellow-800">
            Remark for Manager* (Reason for Extension)
          </span>
          <textarea
            value={managerRemark}
            onChange={(e) => setManagerRemark(e.target.value)}
            placeholder="State the clear reason for the extension (e.g., requires further observation in X area)."
            rows={3}
            className="mt-1 block w-full rounded-md border-red-300 shadow-sm focus:border-red-500 focus:ring focus:ring-red-500 focus:ring-opacity-50 p-2"
          />
        </label>
      </div>

      <div className="flex justify-end space-x-3 mt-4 pt-3 border-t border-yellow-300">
        <button
          onClick={onCancel}
          className="text-sm font-medium text-gray-700 hover:text-gray-900 px-3 py-2 rounded-lg transition"
        >
          Cancel
        </button>
        {/* Dimmer Button Color */}
        <button
          onClick={handleSubmit}
          disabled={!extendTill || !managerRemark.trim()}
          className="bg-yellow-700 hover:bg-yellow-800 text-white font-semibold text-sm py-2 px-4 rounded-lg shadow-md transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Send className="h-4 w-4 mr-1 inline-block" /> Submit Extension
        </button>
      </div>
    </div>
  );
};

// --- MAIN MODAL COMPONENT ---

const ConfirmationDetailModal: React.FC<ConfirmationDetailModalProps> = ({
  isOpen,
  onClose,
  confirmations,
}) => {
  const dispatch = useDispatch<RootDispatch>();

  // State to track which employee is being acted upon and what action is chosen
  const [selectedAction, setSelectedAction] = useState<{
    employeeId: string;
    action: "confirm" | "extend";
  } | null>(null);

  if (!isOpen) return null;

  const modalTitle = `Pending Confirmations (${confirmations.length})`;

  // Handler for Confirm action
  const handleConfirm = (payload: any) => {
    dispatch(confirmEmployee(payload));
    setSelectedAction(null);
  };

  // Handler for Extend action
  const handleExtend = (payload: any) => {
    dispatch(extendProbation(payload));
    setSelectedAction(null);
  };

  return (
    // Modal Wrapper
    <div className="fixed inset-0 bg-gray-900/85 backdrop-blur-sm flex justify-center items-center p-4 z-50 transition-opacity duration-300">
      {/* Modal Container */}
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] flex flex-col overflow-hidden transform scale-100 transition-transform duration-300">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-20">
          <h2 className="text-3xl font-extrabold text-gray-800 flex items-center tracking-tight">
            <CheckCircle className="h-7 w-7 mr-3 text-blue-600" />
            <span className="leading-tight">{modalTitle}</span>
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-slate-700 p-2 rounded-full transition-all duration-200 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-slate-400"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50 space-y-6">
          {confirmations.length > 0 ? (
            confirmations.map((emp) => {
              const isActive = selectedAction?.employeeId === emp._id;
              const isConfirm = selectedAction?.action === "confirm";

              return (
                <div
                  key={emp._id}
                  className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-blue-500"
                >
                  {/* Employee Name/Title */}
                  <h3 className="text-xl font-bold text-gray-800 flex items-center mb-4 pb-2 border-b border-gray-200">
                    <User className="h-5 w-5 mr-3 text-blue-500" />
                    {emp.name}{" "}
                    <span className="text-sm font-medium text-slate-500 ml-3">
                      / {emp.designation}
                    </span>
                  </h3>

                  {/* HR Data Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <DetailItem
                      label="Joining Date"
                      value={format(new Date(emp.dateOfJoining), "dd MMM yyyy")}
                      icon={Calendar}
                      color="text-emerald-600"
                    />
                    <DetailItem
                      label="Confirmation Date"
                      value={format(
                        new Date(emp.dateOfConfirmation),
                        "dd MMM yyyy"
                      )}
                      icon={CheckCircle}
                      color="text-blue-600"
                    />
                    <DetailItem
                      label="Probation Period"
                      value={`${emp.probationDays || "N/A"} days`}
                      icon={Briefcase}
                      color="text-yellow-600" // Adjusted for consistency
                    />
                    <DetailItem
                      label="Employment Status"
                      value={emp.employmentStatus}
                      icon={User}
                      color="text-slate-500"
                    />
                    <DetailItem
                      label="Department"
                      value={emp.department || "N/A"}
                      icon={Briefcase}
                      color="text-slate-500"
                    />
                    <DetailItem
                      label="Location"
                      value={emp.location || "N/A"}
                      icon={MapPin}
                      color="text-slate-500"
                    />
                  </div>

                  {/* Action Section */}
                  <div className="mt-6 pt-4 border-t border-gray-100 flex justify-end w-full">
                    {isActive ? (
                      isConfirm ? (
                        <ConfirmationSection
                          employee={emp}
                          onCancel={() => setSelectedAction(null)}
                          onConfirm={handleConfirm}
                        />
                      ) : (
                        <ExtensionSection
                          employee={emp}
                          onCancel={() => setSelectedAction(null)}
                          onExtend={handleExtend}
                        />
                      )
                    ) : (
                      // Action Buttons when no action is selected
                      <div className="flex space-x-3">
                        <button
                          onClick={() =>
                            setSelectedAction({
                              employeeId: emp._id,
                              action: "extend",
                            })
                          }
                          className="bg-yellow-600 hover:bg-yellow-700 text-white font-semibold py-2 px-5 rounded-lg shadow-md transition duration-200 flex items-center focus:outline-none focus:ring-4 focus:ring-yellow-300"
                        >
                          <Clock className="h-5 w-5 mr-2" /> Extend
                        </button>
                        <button
                          onClick={() =>
                            setSelectedAction({
                              employeeId: emp._id,
                              action: "confirm",
                            })
                          }
                          className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-5 rounded-lg shadow-md transition duration-200 flex items-center focus:outline-none focus:ring-4 focus:ring-green-300"
                        >
                          <CheckCircle className="h-5 w-5 mr-2" /> Confirm
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center p-10 bg-white rounded-xl shadow-lg border border-gray-200">
              <CheckCircle className="h-10 w-10 mx-auto text-gray-400 mb-3" />
              <p className="text-lg font-medium text-gray-600">
                No pending confirmations at this time.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 text-sm text-slate-500 bg-white sticky bottom-0 z-10 text-center">
          Either confirm or extend the employee with valid reasons or they will
          be confirmed automatically on Confirmation Date
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDetailModal;
